import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';

export default function AdminCommunications() {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchCommunications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, user, navigate]);

  const fetchCommunications = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter !== 'tous') params.append('statut', filter);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/communications?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCommunications(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, statut) => {
    const message = statut === 'programme' 
      ? 'Êtes-vous sûr de vouloir supprimer cette communication programmée ? Elle ne sera pas envoyée.'
      : 'Êtes-vous sûr de vouloir supprimer ce brouillon ?';
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/communications/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchCommunications();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'envoye': 'bg-green-100 text-green-800',
      'brouillon': 'bg-gray-100 text-gray-800',
      'programme': 'bg-blue-100 text-blue-800'
    };
    const labels = {
      'envoye': 'Envoyé',
      'brouillon': 'Brouillon',
      'programme': 'Programmé'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[statut]}`}>
        {labels[statut]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Communications</h1>
              <p className="mt-2 text-sm text-gray-600">
                Envoyez des emails à vos adhérents
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <HelpButton 
                title={helpContent.communications.title}
                sections={helpContent.communications.sections}
              />
              <button
                onClick={() => navigate('/admin/communications/nouveau')}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Nouvelle communication
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filtrer :</span>
            {['tous', 'envoye', 'brouillon', 'programme'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === f
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'tous' ? 'Tous' : f === 'envoye' ? 'Envoyés' : f === 'brouillon' ? 'Brouillons' : 'Programmés'}
              </button>
            ))}
          </div>
        </div>

        {/* Communications List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {communications.length === 0 ? (
            <div className="text-center py-12">
              <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune communication</h3>
              <p className="mt-1 text-sm text-gray-500">
                Commencez par créer une nouvelle communication.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Auteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Envois
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {communications.map((comm) => (
                  <tr key={comm._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {comm.estSanitaire && (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {comm.titre}
                            {comm.piecesJointes?.length > 0 && (
                              <span className="ml-2 inline-flex items-center text-gray-400" title={`${comm.piecesJointes.length} pièce(s) jointe(s)`}>
                                <PaperClipIcon className="h-4 w-4" />
                                <span className="ml-0.5 text-xs">{comm.piecesJointes.length}</span>
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {comm.estSanitaire
                              ? 'Intérêt général'
                              : [
                                  ...(comm.criteresDestinataires || []).map((c) => c.organisme),
                                  ...(comm.groupesDestinataires || []).map((g) =>
                                    `${g.organisme} ${g.groupe === 'bureau' ? 'Bureau' : g.organisme === 'SAR' ? 'CS' : 'CA'}`
                                  ),
                                ].filter((v, i, a) => a.indexOf(v) === i).join(' · ') || 'Aucun destinataire'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-sm text-gray-900">
                        {comm.auteur?.prenom} {comm.auteur?.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatutBadge(comm.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {comm.statut === 'envoye' && comm.dateEnvoi && (
                        <div className="flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-1" />
                          {formatDate(comm.dateEnvoi)}
                        </div>
                      )}
                      {comm.statut === 'programme' && comm.dateProgrammee && (
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDate(comm.dateProgrammee)}
                        </div>
                      )}
                      {comm.statut === 'brouillon' && (
                        <div className="text-gray-400">-</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {comm.statut === 'envoye' ? (
                        <div>
                          <div className="text-green-600 font-medium">
                            {comm.emailsEnvoyes} envoyés
                          </div>
                          {comm.emailsEchoues > 0 && (
                            <div className="text-red-600 text-xs">
                              {comm.emailsEchoues} échecs
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-400">-</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/admin/communications/${comm._id}`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Voir"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {(comm.statut === 'brouillon' || comm.statut === 'programme') && (
                          <>
                            <button
                              onClick={() => navigate(`/admin/communications/edit/${comm._id}`)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Modifier"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(comm._id, comm.statut)}
                              className="text-red-600 hover:text-red-900"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
